import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DatePicker, Form, Input, InputNumber, Select } from 'antd';

import dayjs from 'dayjs';
import equal from 'fast-deep-equal';

import { DATE_FORMAT } from '../../constants/common';
import { MAIN_GROUP_TYPE } from '../../constants/group';
import messages from '../../constants/messages';
import { PLAN_TYPE } from '../../constants/subscription';

import { fetchLabels } from '../../redux/actions/label';

import { errorToastHandler } from '../../components/action_notifier';

const emptyFormValues = {
  assigned_groups: [],
  geofence_id: null,
  rotation_name: null,
  rotation_start_date: dayjs(),
  rotation_end_date: dayjs(),
  forage_cover_before_rotation: null,
  forage_cover_after_rotation: null,
  forage_height_before_rotation: null,
  forage_height_after_rotation: null,
  biomass_weight_before_rotation: null,
  biomass_weight_after_rotation: null
};

function RotationForm({ className, formRef, isEditMode = true, onChange, setSubmittable, initialRotationValues }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [form] = Form.useForm();
  if (formRef) formRef.current = form;
  const values = Form.useWatch([], form);

  const { pastures } = useSelector(state => state.geofence);
  const { options: groups, loading: groupsLoading } = useSelector(state => state.label);
  const { myPlan } = useSelector(state => state.subscription);
  const { isAdmin } = useSelector(state => state.user);

  const [initialFormValues, setInitialFormValues] = useState({});

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    const geofence_id = form.getFieldValue('geofence_id');
    const geofence = pastures?.find(p => p.id === geofence_id);

    if (!geofence?.farm_id) return;

    getGroups({ farm_id: geofence?.farm_id, main_type: MAIN_GROUP_TYPE.PHYSICAL });
  }, [pastures]);

  useEffect(() => {
    if (!initialRotationValues) {
      return;
    }

    form.resetFields();

    form.setFieldsValue({
      rotation_start_date: dayjs(initialRotationValues.rotation_start_date),
      rotation_end_date: dayjs(initialRotationValues.rotation_end_date)
    });
  }, [initialRotationValues]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable?.(true))
      .catch(() => setSubmittable?.(false));
  }, [values]);

  async function getGroups({ farm_id, main_type }) {
    try {
      await dispatch(fetchLabels({ farm_ids: [farm_id], main_type })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function handleChange(changedValues) {
    if ('geofence_id' in changedValues) {
      const geofence = pastures?.find(p => p.id === changedValues.geofence_id);

      if (!geofence) return;

      await getGroups({ farm_id: geofence?.farm_id, main_type: MAIN_GROUP_TYPE.PHYSICAL });
    }

    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
  }

  const minDate = formRef.current?.getFieldValue('rotation_start_date');
  const pastureOptions = useMemo(() => {
    const currentId = initialRotationValues?.geofence_id;
    const currentName = initialRotationValues?.pasture?.name;
    const existsInList = pastures?.find(opt => opt.id === currentId);

    const options = pastures?.map(p => ({ label: p.name, value: p.id }));
    // add master pasture as a disabled option to show the correct name
    if (!existsInList && currentId) {
      return [
        {
          value: currentId,
          label: currentName || 'Unknown',
          disabled: true
        },
        ...options,
      ]
    }
    return options;
  }, [pastures]);

  function prepareFormValues() {
    const defaultGeofence = pastureOptions?.find(g => g.value === +id);

    return isEditMode
      ? {
          ...emptyFormValues,
          geofence_id: defaultGeofence?.value ?? null
        }
      : {
          ...initialRotationValues,
          assigned_groups: initialRotationValues?.assigned_groups?.map(group => {
            if (typeof group === 'number') return group;
            return group?.label_id;
          }),
          rotation_start_date: dayjs(initialRotationValues.rotation_start_date),
          rotation_end_date: dayjs(initialRotationValues.rotation_end_date)
        };
  }

  return (
    <Form
      autoComplete="off"
      layout="vertical"
      variant="outlined"
      className={className}
      form={form}
      initialValues={prepareFormValues()}
      requiredMark={isEditMode}
      onValuesChange={handleChange}>
      <Form.Item
        label="Pasture"
        name="geofence_id"
        rules={[{ required: true, message: messages.FIELD_REQUIRED('Pasture') }]}>
        <Select disabled={!isEditMode || id} options={pastureOptions} />
      </Form.Item>
      <Form.Item
        label="Rotation name"
        name="rotation_name"
        rules={[{ required: true, message: messages.FIELD_REQUIRED('Rotation name') }]}>
        <Input disabled={!isEditMode} />
      </Form.Item>
      <Form.Item
        label="Rotation start date"
        name="rotation_start_date"
        rules={[{ required: true, message: messages.FIELD_REQUIRED('Rotation start date') }]}>
        <DatePicker
          disabled={!isEditMode}
          id="due-date"
          className="w-full"
          variant="outlined"
          allowClear={false}
          format={DATE_FORMAT.DATE}
        />
      </Form.Item>
      <Form.Item
        label="Rotation end date"
        name="rotation_end_date"
        rules={[{ required: true, message: messages.FIELD_REQUIRED('Rotation end date') }]}>
        <DatePicker
          disabled={!isEditMode}
          id="due-date"
          className="w-full"
          variant="outlined"
          allowClear={false}
          disabledDate={current => current && current.isBefore(minDate, 'day')}
          format={DATE_FORMAT.DATE}
        />
      </Form.Item>
      <Form.Item label="Select group(s) for this rotation" name="assigned_groups">
        <Select
          allowClear={true}
          labelInValue={false}
          loading={groupsLoading}
          disabled={!isEditMode || groupsLoading || !form.getFieldValue('geofence_id')}
          options={groups}
          mode="multiple"
        />
      </Form.Item>

      {(myPlan?.type === PLAN_TYPE.ADVANCED || isAdmin) && (
        <>
          <Form.Item
            label="Forage cover before rotation"
            name="forage_cover_before_rotation"
            rules={[{ type: 'number', message: messages.FIELD_INVALID('Forage cover before rotation') }]}>
            <InputNumber disabled={!isEditMode} changeOnWheel className="w-full" min={0} />
          </Form.Item>
          <Form.Item
            label="Forage cover after rotation"
            name="forage_cover_after_rotation"
            rules={[{ type: 'number', message: messages.FIELD_INVALID('Forage cover after rotation') }]}>
            <InputNumber disabled={!isEditMode} changeOnWheel className="w-full" min={0} />
          </Form.Item>
          <Form.Item
            label="Forage height before rotation"
            name="forage_height_before_rotation"
            rules={[{ type: 'number', message: messages.FIELD_INVALID('Forage height before rotation') }]}>
            <InputNumber disabled={!isEditMode} changeOnWheel className="w-full" min={0} />
          </Form.Item>
          <Form.Item
            label="Forage height after rotation"
            name="forage_height_after_rotation"
            rules={[{ type: 'number', message: messages.FIELD_INVALID('Forage height after rotation') }]}>
            <InputNumber disabled={!isEditMode} changeOnWheel className="w-full" min={0} />
          </Form.Item>
          <Form.Item
            label="Biomass weight before rotation"
            name="biomass_weight_before_rotation"
            rules={[{ type: 'number', message: messages.FIELD_INVALID('Biomass weight before rotation') }]}>
            <InputNumber disabled={!isEditMode} changeOnWheel className="w-full" min={0} />
          </Form.Item>
          <Form.Item
            label="Biomass weight after rotation"
            name="biomass_weight_after_rotation"
            rules={[{ type: 'number', message: messages.FIELD_INVALID('Biomass weight after rotation') }]}>
            <InputNumber disabled={!isEditMode} changeOnWheel className="w-full" min={0} />
          </Form.Item>
        </>
      )}
    </Form>
  );
}

export default memo(RotationForm);
